if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
          if (this.contains(target)) {
            this.setupQuickAddGalleryVariantSync(target);
            this.setupVariantDescriptionInModal(target);
            this.setupQuickAddGallerySwipe(target);
            this.setupQuickAddThumbnailSwipe(target);
          }
        });
      }

      /* Touch-Swipe fuer Hauptbild im Quick-Add-Modal (Handy).
         Native Scroll + Snap funktionieren in genesteten Scroll-Containern (iOS Safari)
         nicht zuverlaessig durchgereicht, daher: touch komplett in JS uebernehmen,
         scrollLeft manuell setzen, am Ende per scrollIntoView zum naechsten Slide. */
      setupQuickAddGallerySwipe(productInfoEl) {
        if (!productInfoEl) return;

        const mobileMql = window.matchMedia('(max-width: 749px)');

        const clearStyles = (galleryList) => {
          ['display','flexWrap','overflowX','overflowY','scrollSnapType','touchAction','webkitOverflowScrolling','scrollBehavior']
            .forEach((k) => { galleryList.style[k] = ''; });
          galleryList.querySelectorAll('[id^="Slide-"]').forEach((slide) => {
            slide.style.flex = '';
            slide.style.scrollSnapAlign = '';
          });
        };

        const applyStyles = (galleryList) => {
          if (!mobileMql.matches) {
            /* Desktop: keine Inline-Overrides — CSS regelt das Layout */
            clearStyles(galleryList);
            return;
          }
          Object.assign(galleryList.style, {
            display: 'flex',
            flexWrap: 'nowrap',
            overflowX: 'auto',
            overflowY: 'hidden',
            scrollSnapType: 'none', /* JS uebernimmt Snap — natives Snap wuerde dagegen kaempfen */
            touchAction: 'pan-y',   /* horizontale Geste wird per preventDefault von JS geroutet */
            webkitOverflowScrolling: 'touch',
            scrollBehavior: 'smooth',
          });
          galleryList.querySelectorAll('[id^="Slide-"]').forEach((slide) => {
            slide.style.flex = '0 0 auto';
            slide.style.scrollSnapAlign = 'none';
          });
        };

        const bind = () => {
          const galleryList = productInfoEl.querySelector('[id^="Slider-Gallery"]');
          if (!galleryList) return;

          /* Styles bei jedem Re-Bind neu setzen (Slides werden bei Variantenwechsel ausgetauscht) */
          applyStyles(galleryList);

          /* Touch-Listener nur auf Mobile anhaengen; auf Desktop ist der native Klick-Flow aktiv */
          if (!mobileMql.matches) return;
          if (galleryList.dataset.qaSwipeBound === '1') return;
          galleryList.dataset.qaSwipeBound = '1';

          let startX = 0;
          let startY = 0;
          let startScrollLeft = 0;
          let axis = null; /* 'x' | 'y' | null */
          let active = false;

          galleryList.addEventListener(
            'touchstart',
            (e) => {
              if (e.touches.length !== 1) return;
              startX = e.touches[0].clientX;
              startY = e.touches[0].clientY;
              startScrollLeft = galleryList.scrollLeft;
              axis = null;
              active = true;
            },
            { passive: true }
          );

          galleryList.addEventListener(
            'touchmove',
            (e) => {
              if (!active) return;
              const dx = e.touches[0].clientX - startX;
              const dy = e.touches[0].clientY - startY;
              if (axis === null) {
                if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
                axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
              }
              if (axis === 'x') {
                e.preventDefault();
                /* smooth scroll deaktivieren waehrend des Drags damit das Bild dem Finger folgt */
                galleryList.style.scrollBehavior = 'auto';
                galleryList.scrollLeft = startScrollLeft - dx;
              }
            },
            { passive: false }
          );

          const endHandler = () => {
            if (!active) return;
            active = false;
            galleryList.style.scrollBehavior = 'smooth';
            if (axis !== 'x') return;

            const slides = Array.from(galleryList.querySelectorAll('[id^="Slide-"]'));
            if (slides.length < 2) {
              galleryList.scrollTo({ left: startScrollLeft, behavior: 'smooth' });
              return;
            }

            const totalDx = galleryList.scrollLeft - startScrollLeft;
            const threshold = 30; /* px — kleine Wische reichen */

            /* Stride = Abstand zwischen zwei aufeinanderfolgenden Slides (inkl. Margin/Gap).
               Invariant gegenueber aktuellem scrollLeft, weil beide Slides simultan verschoben sind. */
            const r0 = slides[0].getBoundingClientRect();
            const r1 = slides[1].getBoundingClientRect();
            const stride = Math.abs(r1.left - r0.left) || galleryList.clientWidth;

            /* Start-Slide: derjenige, dessen linke Kante bei startScrollLeft am Container-Rand war */
            const startIndex = Math.max(0, Math.min(slides.length - 1, Math.round(startScrollLeft / stride)));

            let targetIndex = startIndex;
            if (totalDx > threshold) targetIndex = Math.min(startIndex + 1, slides.length - 1);
            else if (totalDx < -threshold) targetIndex = Math.max(startIndex - 1, 0);

            const target = slides[targetIndex];
            /* scrollIntoView handhabt Margins/Scroll-Padding korrekt, ohne block=nearest wird nicht vertikal gescrollt */
            if (typeof target.scrollIntoView === 'function') {
              target.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
            } else {
              galleryList.scrollTo({ left: targetIndex * stride, behavior: 'smooth' });
            }

            /* media-gallery aktive Klasse synchronisieren, damit Thumbnails korrekt markiert bleiben */
            const mediaGallery = galleryList.closest('media-gallery');
            const mediaId = target.dataset.mediaId || (target.id || '').replace(/^Slide-/, '');
            if (mediaGallery && mediaId && typeof mediaGallery.setActiveMedia === 'function') {
              try { mediaGallery.setActiveMedia(mediaId, false); } catch (err) { /* noop */ }
            }
          };
          galleryList.addEventListener('touchend', endHandler, { passive: true });
          galleryList.addEventListener('touchcancel', endHandler, { passive: true });
        };

        bind();
        /* Re-bind nach Variantenwechsel (innerHTML des Galleryviewers wird ersetzt) */
        const galleryViewer = productInfoEl.querySelector('[id^="GalleryViewer"]');
        if (galleryViewer && galleryViewer.dataset.qaSwipeObserved !== '1') {
          galleryViewer.dataset.qaSwipeObserved = '1';
          const observer = new MutationObserver(() => bind());
          observer.observe(galleryViewer, { childList: true, subtree: true });
        }
      }

      /* Touch-Drag-Scroll fuer Thumbnail-Liste im Quick-Add-Modal (Handy).
         Freies horizontales Scrollen ohne Snap — der User soll nur alle verfuegbaren
         Thumbnails durchsehen koennen; das aktive Bild wird weiterhin per Tap gewaehlt. */
      setupQuickAddThumbnailSwipe(productInfoEl) {
        if (!productInfoEl) return;
        const mobileMql = window.matchMedia('(max-width: 749px)');
        if (!mobileMql.matches) return;

        const bind = () => {
          const thumbList = productInfoEl.querySelector('[id^="Slider-Thumbnails"]');
          if (!thumbList) return;

          /* Inline-Styles, damit die Liste sicher horizontal scrollbar ist */
          Object.assign(thumbList.style, {
            display: 'flex',
            flexWrap: 'nowrap',
            overflowX: 'auto',
            overflowY: 'hidden',
            touchAction: 'pan-x pan-y',
            webkitOverflowScrolling: 'touch',
            scrollBehavior: 'smooth',
          });
          thumbList.querySelectorAll('[id^="Slide-Thumbnails"]').forEach((item) => {
            item.style.flex = '0 0 auto';
          });

          if (thumbList.dataset.qaThumbSwipeBound === '1') return;
          thumbList.dataset.qaThumbSwipeBound = '1';

          let startX = 0;
          let startY = 0;
          let startScrollLeft = 0;
          let axis = null;
          let active = false;
          let moved = false;

          thumbList.addEventListener(
            'touchstart',
            (e) => {
              if (e.touches.length !== 1) return;
              startX = e.touches[0].clientX;
              startY = e.touches[0].clientY;
              startScrollLeft = thumbList.scrollLeft;
              axis = null;
              active = true;
              moved = false;
              thumbList.style.scrollBehavior = 'auto';
            },
            { passive: true }
          );

          thumbList.addEventListener(
            'touchmove',
            (e) => {
              if (!active) return;
              const dx = e.touches[0].clientX - startX;
              const dy = e.touches[0].clientY - startY;
              if (axis === null) {
                if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
                axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
              }
              if (axis === 'x') {
                e.preventDefault();
                thumbList.scrollLeft = startScrollLeft - dx;
                if (Math.abs(dx) > 8) moved = true;
              }
            },
            { passive: false }
          );

          const endHandler = (e) => {
            if (!active) return;
            active = false;
            thumbList.style.scrollBehavior = 'smooth';
            /* Wenn wirklich gewischt wurde: Klick auf Thumbnail verhindern, damit
               nicht versehentlich ein Bild beim Drag-Ende ausgewaehlt wird. */
            if (moved && e && e.target) {
              const blocker = (ev) => { ev.stopPropagation(); ev.preventDefault(); };
              thumbList.addEventListener('click', blocker, { capture: true, once: true });
              setTimeout(() => thumbList.removeEventListener('click', blocker, { capture: true }), 300);
            }
          };
          thumbList.addEventListener('touchend', endHandler, { passive: true });
          thumbList.addEventListener('touchcancel', endHandler, { passive: true });
        };

        bind();
        /* Nach Variantenwechsel (innerHTML-Austausch) erneut binden */
        const thumbSlider = productInfoEl.querySelector('slider-component.thumbnail-slider');
        if (thumbSlider && thumbSlider.dataset.qaThumbObserved !== '1') {
          thumbSlider.dataset.qaThumbObserved = '1';
          const observer = new MutationObserver(() => bind());
          observer.observe(thumbSlider, { childList: true, subtree: true });
        }
      }

      setupVariantDescriptionInModal(productInfoEl) {
        const parseVariantData = () => {
          const scriptEl = productInfoEl.querySelector('script.variant-descriptions-data');
          if (!scriptEl) return null;
          try {
            const data = JSON.parse(scriptEl.textContent || '{}');
            return (data && data.variants) ? data.variants : {};
          } catch (e) {
            return null;
          }
        };

        let variantDescriptions = parseVariantData();
        if (variantDescriptions === null) return;

        let productDescriptionOriginal = '';
        let contentNode = null;

        const getCurrentVariantId = () => {
          const mainForm = productInfoEl.querySelector('form[action*="/cart/add"]') || productInfoEl.querySelector('form.product-form') || productInfoEl.querySelector('form.form');
          const scope = mainForm || productInfoEl;
          const input = scope.querySelector('input[name="id"]:checked') || scope.querySelector('input[name="id"]') || productInfoEl.querySelector('input[name="id"]');
          return input ? String(input.value).trim() : null;
        };

        const updateDescription = () => {
          if (!productInfoEl.isConnected) return;
          if (!this.modalContent.contains(productInfoEl)) return;
          const container = productInfoEl.querySelector('#product-description-container');
          const content = productInfoEl.querySelector('#product-description-content');
          if (!container || !content) return;

          if (content !== contentNode) {
            contentNode = content;
            productDescriptionOriginal = content.innerHTML.trim();
            variantDescriptions = parseVariantData() || variantDescriptions;
          }

          const variantId = getCurrentVariantId();
          const variantDescription = variantId ? variantDescriptions[variantId] : null;
          if (variantDescription && String(variantDescription).trim() !== '') {
            content.innerHTML = variantDescription;
          } else if (productDescriptionOriginal) {
            content.innerHTML = productDescriptionOriginal;
          }
          container.style.display = '';
          if (typeof window.openDefaultAccordions === 'function') {
            window.openDefaultAccordions(productInfoEl, true);
          }
          const accordionButtons = container.querySelectorAll('.bidex-accordion-button');
          accordionButtons.forEach((btn) => {
            btn.onclick = function () {
              if (typeof window.toggleBidexAccordion === 'function') window.toggleBidexAccordion(this);
            };
          });
        };

        // Bereits eingerichtet (z. B. verzögerter Aufruf): nur Beschreibung neu setzen, keine doppelten Listener
        if (productInfoEl.dataset.variantDescriptionSetup === '1') {
          updateDescription();
          requestAnimationFrame(updateDescription);
          setTimeout(updateDescription, 50);
          setTimeout(updateDescription, 150);
          return;
        }
        productInfoEl.dataset.variantDescriptionSetup = '1';

        updateDescription();
        requestAnimationFrame(() => updateDescription());
        setTimeout(updateDescription, 50);
        setTimeout(updateDescription, 150);
        setTimeout(updateDescription, 350);

        const form = productInfoEl.querySelector('form[action*="/cart/add"]') || productInfoEl.querySelector('.product-form');
        if (form) {
          form.addEventListener('change', (e) => {
            if (e.target && (e.target.name === 'id' || e.target.closest('[name="id"]'))) {
              setTimeout(updateDescription, 50);
            }
          });
          form.addEventListener('variant:change', () => setTimeout(updateDescription, 50));
        }
        const boundUpdate = () => {
          if (productInfoEl.isConnected && this.modalContent.contains(productInfoEl)) setTimeout(updateDescription, 50);
        };
        document.addEventListener('variant:change', boundUpdate);
        productInfoEl.addEventListener('variant:change', boundUpdate);
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        if (this.modalContent) this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;

        // Scroll-Position wiederherstellen bevor position:fixed entfernt wird
        const scrollY = parseInt(document.body.dataset.scrollY || '0', 10);
        document.body.style.top = '';
        document.body.classList.remove('overflow-hidden');
        window.scrollTo(0, scrollY);

        document.body.dispatchEvent(new CustomEvent('modalClosed'));
        this.removeAttribute('open');
        removeTrapFocus(this.openedBy);
      }

      show(opener) {
        // Lazy init: re-query modalContent if not found during construction (dynamic insertion)
        if (!this.modalContent) {
          this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        }
        if (!this.modalContent) {
          console.warn('[quick-add-modal] modalContent not found, cannot show');
          return;
        }

        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        const spinner = opener.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            if (productElement) this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement ? productElement.outerHTML : responseText);

            // Variantenbeschreibung nach DOM-Setup explizit setzen (behebt falsche Beschreibung beim 2. Modal)
            const productInfoInModal = this.modalContent.querySelector('product-info');
            if (productInfoInModal) {
              const runDescription = () => {
                if (productInfoInModal.isConnected && this.modalContent.contains(productInfoInModal)) {
                  this.setupVariantDescriptionInModal(productInfoInModal);
                }
              };
              setTimeout(runDescription, 0);
              setTimeout(runDescription, 100);
              setTimeout(runDescription, 250);
            }

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            // Scroll-Position speichern bevor body fixiert wird
            this.openedBy = opener;
            const scrollY = window.scrollY;
            document.body.dataset.scrollY = scrollY;
            document.body.style.top = `-${scrollY}px`;

            const popup = this.querySelector('.template-popup');
            document.body.classList.add('overflow-hidden');
            this.setAttribute('open', '');
            if (popup) popup.loadContent();
            trapFocus(this, this.querySelector('[role="dialog"]'));
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            const sp = opener.querySelector('.loading__spinner');
            if (sp) sp.classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        if (!productElement || !this.modalContent) return;
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      setupQuickAddGalleryVariantSync(productInfoEl) {
        if (!productInfoEl || productInfoEl.dataset.galleryVariantSync === '1') return;
        productInfoEl.dataset.galleryVariantSync = '1';
        const modal = this;
        const onVariantChange = () => {
          if (!modal.modalContent.contains(productInfoEl)) return;
          const variantIdInput = productInfoEl.querySelector('input[name="id"]');
          const variantId = variantIdInput ? variantIdInput.value : null;
          if (!variantId) return;
          const sectionId = productInfoEl.dataset.section || productInfoEl.id || '';
          const originalSection = productInfoEl.dataset.originalSection || sectionId.replace(/^quickadd-/, '');
          const productUrl = (productInfoEl.dataset.url || productInfoEl.querySelector('form[action*="/cart/add"]')?.action || '').split('?')[0];
          if (!productUrl) return;
          const url = `${productUrl}?variant=${variantId}&section_id=${originalSection}`;
          fetch(url)
            .then((res) => res.text())
            .then((htmlText) => {
              const doc = new DOMParser().parseFromString(htmlText, 'text/html');
              const sourceProductInfo = doc.querySelector('product-info');
              if (!sourceProductInfo) return;
              const scriptEl = sourceProductInfo.querySelector('script[data-selected-variant]');
              let featuredMediaId = null;
              if (scriptEl) {
                try {
                  const variant = JSON.parse(scriptEl.textContent || '{}');
                  featuredMediaId = variant.featured_media && variant.featured_media.id;
                } catch (e) {}
              }
              const sourceGallery = sourceProductInfo.querySelector('media-gallery');
              const destGallery = productInfoEl.querySelector('media-gallery');
              if (!sourceGallery || !destGallery) return;
              const oldId = originalSection;
              const newId = sectionId;
              const thumbSource = sourceGallery.querySelector('[id^="Slider-Thumbnails"]');
              const viewerWrapperSource = sourceGallery.querySelector('[id^="GalleryViewer"]');
              const viewerListSource = sourceGallery.querySelector('[id^="Slider-Gallery"]');
              const thumbDest = destGallery.querySelector('[id^="Slider-Thumbnails"]');
              const viewerWrapperDest = destGallery.querySelector('[id^="GalleryViewer"]');
              const replaceIds = (html) => (html || '').replaceAll(oldId, newId);
              if (thumbSource && thumbDest) {
                thumbDest.innerHTML = replaceIds(thumbSource.innerHTML);
              }
              if (viewerWrapperSource && viewerWrapperDest) {
                viewerWrapperDest.innerHTML = replaceIds(viewerWrapperSource.innerHTML);
              } else if (viewerListSource && viewerWrapperDest) {
                const viewerListDest = viewerWrapperDest.querySelector('[id^="Slider-Gallery"]');
                if (viewerListDest) viewerListDest.innerHTML = replaceIds(viewerListSource.innerHTML);
              }
              /* mediaId: zuerst aus Varianten-Script, sonst aus erstem Thumbnail (Server rendert Featured-Medium zuerst) */
              let mediaId = featuredMediaId ? `${sectionId}-${featuredMediaId}` : null;
              if (!mediaId && thumbDest) {
                const firstThumb = thumbDest.querySelector('li[data-target]');
                if (firstThumb) mediaId = firstThumb.getAttribute('data-target');
              }
              /* Hauptbild im Viewer immer mit dem Bild des ersten Thumbnails (Variantenbild) überschreiben */
              function syncMainImageFromFirstThumbnail() {
                const firstThumb = destGallery.querySelector('[id^="Slider-Thumbnails"] li[data-target]');
                const thumbImg = firstThumb && firstThumb.querySelector('img');
                const activeSlide = destGallery.querySelector('[id^="Slider-Gallery"] .product__media-item.is-active');
                const mainImg = activeSlide ? activeSlide.querySelector('img') : destGallery.querySelector('[id^="Slider-Gallery"] .product__media-item img');
                if (!thumbImg || !mainImg) return;
                let hiResSrc = (thumbImg.src || thumbImg.getAttribute('src') || '').replace(/\bwidth=\d+/i, 'width=2000');
                if (hiResSrc.indexOf('width=') === -1) hiResSrc = hiResSrc + (hiResSrc.indexOf('?') >= 0 ? '&' : '?') + 'width=2000';
                mainImg.src = hiResSrc;
                if (mainImg.srcset !== undefined) mainImg.srcset = '';
              }
              function applyActiveViewerSlide() {
                const viewerList = destGallery.querySelector('[id^="Slider-Gallery"]');
                if (!viewerList) return;
                const allSlides = viewerList.querySelectorAll('[data-media-id]');
                let activeSlide = mediaId ? viewerList.querySelector(`[data-media-id="${mediaId}"]`) : null;
                if (!activeSlide && allSlides.length) activeSlide = allSlides[0];
                if (!activeSlide) return;
                const resolvedMediaId = mediaId || activeSlide.getAttribute('data-media-id');
                allSlides.forEach((el) => el.classList.remove('is-active'));
                activeSlide.classList.add('is-active');
                if (activeSlide.parentElement && activeSlide.parentElement.firstChild !== activeSlide) {
                  activeSlide.parentElement.prepend(activeSlide);
                }
                function scrollToSlide(el) {
                  if (!el || typeof el.scrollTo !== 'function') return;
                  el.scrollTo({ left: activeSlide.offsetLeft });
                }
                scrollToSlide(viewerList);
                const viewerWrapper = destGallery.querySelector('[id^="GalleryViewer"]');
                if (viewerWrapper) scrollToSlide(viewerWrapper);
                let parent = viewerList.parentElement;
                while (parent && parent !== destGallery) {
                  if (parent.scrollWidth > parent.clientWidth) scrollToSlide(parent);
                  parent = parent.parentElement;
                }
                if (destGallery.setActiveMedia && resolvedMediaId) {
                  destGallery.setActiveMedia(resolvedMediaId, true);
                }
                syncMainImageFromFirstThumbnail();
              }
              requestAnimationFrame(() => {
                applyActiveViewerSlide();
                syncMainImageFromFirstThumbnail();
                setTimeout(() => { applyActiveViewerSlide(); syncMainImageFromFirstThumbnail(); }, 50);
                setTimeout(() => { applyActiveViewerSlide(); syncMainImageFromFirstThumbnail(); }, 150);
                setTimeout(() => { applyActiveViewerSlide(); syncMainImageFromFirstThumbnail(); }, 350);
              });
            })
            .catch(() => {});
        };
        productInfoEl.addEventListener('variant:change', () => setTimeout(onVariantChange, 80));
        document.addEventListener('variant:change', (e) => {
          if (this.contains(e.target)) setTimeout(onVariantChange, 80);
        });
        const form = productInfoEl.querySelector('form[action*="/cart/add"]') || productInfoEl.querySelector('.product-form');
        const variantSelects = productInfoEl.querySelector('variant-selects');
        if (form) {
          form.addEventListener('change', (e) => {
            if (e.target && (e.target.name === 'id' || e.target.closest('variant-selects') || e.target.closest('.product-form__input'))) {
              setTimeout(onVariantChange, 220);
            }
          });
        }
        if (variantSelects) {
          variantSelects.addEventListener('change', () => setTimeout(onVariantChange, 220));
        }
        const galleryEl = productInfoEl.querySelector('media-gallery');
        if (galleryEl) {
          galleryEl.addEventListener('click', (e) => {
            const thumb = e.target.closest('[data-target]');
            if (!thumb || !thumb.closest('[id^="Slider-Thumbnails"]') || !galleryEl.contains(thumb)) return;
            const targetId = thumb.getAttribute('data-target');
            if (!targetId) return;
            /* prepend=false: Thumbnail-Klick darf die Liste NICHT umsortieren. Nach einem
               Variantenwechsel ist der innerHTML der Galerie ersetzt, wodurch die per-Button-
               Handler aus media-gallery.js (die setActiveMedia(id,false) + stopPropagation nutzen)
               verloren gehen und Klicks auf diesen delegierten Fallback fallen. Mit prepend=true
               wurde das angeklickte Thumbnail nach vorne geschoben -> identisches Verhalten wie
               die per-Button-Handler (kein Reorder). */
            if (galleryEl.setActiveMedia) galleryEl.setActiveMedia(targetId, false);
            /* Fallback: Wenn der Viewer keinen Slide mit dieser media-id hat, sichtbares Hauptbild aus Thumbnail setzen */
            requestAnimationFrame(() => {
              const viewerList = galleryEl.querySelector('[id^="Slider-Gallery"]');
              const targetSlide = viewerList && viewerList.querySelector(`[data-media-id="${targetId}"]`);
              if (targetSlide) return;
              const thumbImg = thumb.querySelector('img');
              const activeSlide = galleryEl.querySelector('[id^="Slider-Gallery"] .product__media-item.is-active');
              const mainImg = activeSlide ? activeSlide.querySelector('img') : galleryEl.querySelector('[id^="Slider-Gallery"] .product__media-item img');
              if (!thumbImg || !mainImg) return;
              let hiResSrc = (thumbImg.src || thumbImg.getAttribute('src') || '').replace(/\bwidth=\d+/i, 'width=2000');
              if (hiResSrc.indexOf('width=') === -1) hiResSrc = hiResSrc + (hiResSrc.indexOf('?') >= 0 ? '&' : '?') + 'width=2000';
              mainImg.src = hiResSrc;
              if (mainImg.srcset !== undefined) mainImg.srcset = '';
            });
          });
        }
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
